import { Tooltip } from '@sgme/ui';
import { counterpartApiConnector } from 'apiConnector/counterpart/counterpart';
import { GimmickSelectorField } from 'components/common-form/builder/form/node/field/GimmickSelectorField';
import { ProductListField } from 'components/common-form/builder/form/node/field/ProductListField';
import { ResetFormButton } from 'components/common-form/components/ResetFormButton';
import { useBlockOrFieldReadOnly } from 'components/common-form/hooks/useBlockOrFieldReadOnly';
import { productSubtypeDataId, productTypeDataId, useData } from 'components/common-form/hooks/useData';
import { ColoredToggleButton } from 'components/shared/ColoredToggleButton';
import { CompactSelector } from 'components/shared/compact-selector';
import { useSalesCounterpartButton } from 'components/shared/compact-selector/use-toggle-button';
import { IconButton } from 'components/shared/IconButton';
import { SalesCounterPartResult } from 'components/shared/SalesCounterPart/SalesCounterPartResult';
import { SelectSearch } from 'components/shared/SelectSearch/SelectSearch';
import { useCopyTextToClipboard } from 'hooks/useCopyToClipboard';
import { IntlKey } from 'locales';
import type { ISalesCounterpart } from 'models/ISalesCounterpart';
import { FC, useCallback, useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { WorkspaceAnalyticCategoryContext } from 'shared/middlewares/analytics/loggerContext';
import styled from 'styled-components';
import { httpClient } from 'utils/http/httpClient';
import { mediaQueryDown } from 'utils/mediaQueries';

const StyledTopBarContainer = styled.div`
  ${mediaQueryDown('md')`
    flex-wrap: wrap !important;

    & > * {
      margin-top: var(--spacing-1);
      margin-bottom: var(--spacing-1);
    }
  `};
`;

const StyledGimmickSelectorField = styled(GimmickSelectorField)`
  ${mediaQueryDown('sm')`
    order: 5;
  `};
`;

interface TopBarProps {
  isNotEditable: boolean;
  hideClone?: boolean;
  hideOpenInBatchWorkspace?: boolean;
  canClone: boolean;
  canOpenInBatchWorkspace?: boolean;
  onTabCloned?: () => void;
  onTabOpenedInBatchWorkspace?: (tabName: string) => void;
  onResetTabClicked: () => void;
  onRefreshDefaultDatesClicked: () => void;
  onRefreshScheduleClicked: () => void;
  canReset: boolean;
  hideReset?: boolean;
  hidePrivateMode?: boolean;
  isOnBehalfUser: boolean;
  gimmickTogglesFieldIds?: string[];
  productSubtype?: string;
}

const isPrivateModeFieldId = 'IsPrivateMode';
const selectedOnBehalfClientId = 'SelectedOnBehalfClient';
const isOnBehalfFieldId = 'IsOnBehalf';

function isSalesCounterpart(
  counterpart: Nullable<Partial<ISalesCounterpart>>,
): counterpart is Nullable<ISalesCounterpart> {
  return counterpart === null || (counterpart?.id !== undefined && counterpart?.label !== undefined);
}

const { fetchSalesCounterparts: onSearch } = counterpartApiConnector(httpClient);

export const TopBar: FC<TopBarProps> = ({
  gimmickTogglesFieldIds,
  canReset,
  hideReset,
  isOnBehalfUser,
  onTabCloned,
  onTabOpenedInBatchWorkspace,
  onResetTabClicked,
  onRefreshDefaultDatesClicked,
  onRefreshScheduleClicked,
  canClone,
  canOpenInBatchWorkspace,
  hideClone,
  hidePrivateMode,
  isNotEditable,
  productSubtype,
}) => {
  const { formatMessage } = useIntl();
  const logAnalyticsCategory = useContext(WorkspaceAnalyticCategoryContext);
  const [isPrivateModeValue, setIsPrivateModeValue] = useData<boolean>(isPrivateModeFieldId);
  const isSelectedClientReadOnly = useBlockOrFieldReadOnly(selectedOnBehalfClientId);
  const isOnBehalfReadOnly = useBlockOrFieldReadOnly(isOnBehalfFieldId);

  const showPrivateMode = !hidePrivateMode;
  const [showClone, showReset] = [!hideClone, !hideReset];

  const cloneTabButtonClicked = useCallback(() => {
    if (canClone && onTabCloned) {
      logAnalytics.trackEvent(logAnalyticsCategory, 'Clone tab');
      onTabCloned();
    }
  }, [onTabCloned, canClone, logAnalyticsCategory]);

  const openInBatchWorkspaceButtonClicked = useCallback(() => {
    if (onTabOpenedInBatchWorkspace) {
      const productSubtypeLabel = formatMessage({
        id: `quote.products.${productSubtype}` as IntlKey,
        defaultMessage: productSubtype,
      });
      const reloadLabel = formatMessage({ id: `batchPricer.tab.reload` as IntlKey });
      const tabName = `${productSubtypeLabel} - ${reloadLabel}`;
      onTabOpenedInBatchWorkspace(tabName);
    }
  }, [onTabOpenedInBatchWorkspace, productSubtype, formatMessage]);

  const [selectedClient, setSelectedClient] = useData<ISalesCounterpart>(selectedOnBehalfClientId);

  const onCounterpartChanged = useCallback(
    (counterpart: Nullable<Partial<ISalesCounterpart>>) => {
      if (isSalesCounterpart(counterpart)) {
        setSelectedClient(counterpart);
      }
    },
    [setSelectedClient],
  );

  const copyToClipboard = useCopyTextToClipboard();
  const isSelectorDisabled = isNotEditable || isSelectedClientReadOnly || isOnBehalfReadOnly;
  const { label, className } = useSalesCounterpartButton(selectedClient);
  return (
    <div id="NewTopBar" className="bg-lvl1 p-2" data-e2e="top-bar">
      <StyledTopBarContainer
        role="toolbar"
        aria-label="Workspace tab toolbar"
        className="d-flex align-items-center px-md-1 justify-content-between"
      >
        <div className="d-flex flex-shrink-0 align-items-center gap-1">
          <div className="d-flex">
            <ProductListField fieldId={productTypeDataId} isDisabled={isNotEditable} />
            <ProductListField fieldId={productSubtypeDataId} isDisabled={isNotEditable} />
          </div>
          {gimmickTogglesFieldIds !== undefined && (
            <StyledGimmickSelectorField fieldIds={gimmickTogglesFieldIds} isDisabled={isNotEditable} className="ms-2" />
          )}
        </div>
        <div className="d-flex flex-grow-1 align-items-center min-width-0 justify-content-end">
          {isOnBehalfUser && (
            <CompactSelector
              renderButton={(ref, onClick) => (
                <CompactSelector.ToggleButton
                  disabled={isSelectorDisabled}
                  id="top-bar-client"
                  e2ePrefix="client"
                  label={label}
                  className={className}
                  onClick={onClick}
                  ref={ref}
                />
              )}
              isDisabled={isSelectorDisabled}
              onSelectedItemChanged={onCounterpartChanged}
              selectedValue={selectedClient}
              renderContent={(onSelectedChanged, selected) => (
                <SelectSearch
                  autofocus
                  getSearchResult={onSearch}
                  onValueSelected={onSelectedChanged}
                  resultRenderer={SalesCounterPartResult}
                >
                  {selected && (
                    <button
                      className="btn btn-outline-secondary btn-icon-end text-nowrap ms-2"
                      onClick={() => copyToClipboard(`${selectedClient?.label!} (${selectedClient?.id!})`)}
                    >
                      <em className="icon ">file_copy</em>
                      <FormattedMessage id="client.copy" />
                    </button>
                  )}
                </SelectSearch>
              )}
            />
          )}

          {isOnBehalfUser && showPrivateMode && (
            <ColoredToggleButton
              id="isPrivateMode"
              iconName={isPrivateModeValue ? 'visibility_off' : 'visibility'}
              labelKey={isPrivateModeValue ? 'form.IsPrivateMode' : 'form.IsPrivateMode.false'}
              value={isPrivateModeValue ?? false}
              isDisabled={isNotEditable}
              onValueChanged={setIsPrivateModeValue}
              className="border-0 flex-shrink-0"
              e2e="IsPrivateMode"
            />
          )}

          {showClone || showReset ? (
            <span className="border-end h-100 pe-2 me-2">
              <br />
            </span>
          ) : null}

          <Tooltip position="bottom" contents={<FormattedMessage id="openInBatchWorkspace" />}>
            <IconButton
              iconName="open_in_new"
              isDisabled={!canOpenInBatchWorkspace}
              onClick={openInBatchWorkspaceButtonClicked}
              e2e="open-in-batch-workspace"
            />
          </Tooltip>
          {showClone && (
            <IconButton
              iconName="filter_none"
              isDisabled={!canClone}
              onClick={cloneTabButtonClicked}
              e2e="clone-tab"
              title={formatMessage({ id: 'form.cloneForm' })}
            />
          )}
          {showReset && (
            <ResetFormButton
              isDisabled={!canReset}
              onResetTabClicked={onResetTabClicked}
              onRefreshDefaultDatesClicked={onRefreshDefaultDatesClicked}
              onRefreshScheduleClicked={onRefreshScheduleClicked}
            />
          )}
        </div>
      </StyledTopBarContainer>
    </div>
  );
};
TopBar.displayName = 'TopBar';
